import { useState } from 'react';
import {
  useDevices,
  useDeviceUpdates,
  useRevokeDevice,
  useRemoveDevice,
  formatDate,
  usePlan,
} from '@redbax/shared';
import type { Device } from '@redbax/shared';
import {
  AnimatedTimestamp,
  PageHeader,
  DataTable,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  type Column,
  UpgradePrompt,
} from '@redbax/ui-kit';
import { CheckCircle2, Clock, Eye, MinusCircle, MoreHorizontal, ShieldOff, Trash2 } from 'lucide-react';
import { AddDeviceButton } from '../components/join-token-card';
import { PendingDevicesCard } from '../components/pending-devices-card';
import { DeviceDetailsDialog } from '../components/device-details-dialog';

const revocableStatuses = new Set(['Online', 'Offline', 'Approved']);
const removableStatuses = new Set(['Revoked', 'Offline', 'Rejected', 'PendingApproval']);

function ActionsCell({ device, onViewDetails }: { device: Device; onViewDetails: () => void }) {
  const revokeDevice = useRevokeDevice();
  const removeDevice = useRemoveDevice();
  const [confirmRemove, setConfirmRemove] = useState(false);

  const canRevoke = revocableStatuses.has(device.status);
  const canRemove = removableStatuses.has(device.status);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex items-center justify-center rounded-md p-2 text-text-secondary hover:bg-surface-raised">
          <MoreHorizontal className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onViewDetails}>
            <Eye className="mr-2 h-4 w-4" />
            Details
          </DropdownMenuItem>
          {(canRevoke || canRemove) && <DropdownMenuSeparator />}
          {canRevoke && (
            <DropdownMenuItem
              onClick={() => !revokeDevice.isPending && revokeDevice.mutate(device.id)}
              className={`text-status-error focus:text-status-error ${revokeDevice.isPending ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <ShieldOff className="mr-2 h-4 w-4" />
              Revoke Device
            </DropdownMenuItem>
          )}
          {canRemove && (
            <DropdownMenuItem
              onClick={() => setConfirmRemove(true)}
              className="text-status-error focus:text-status-error"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove Device
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <div className="fixed inset-0 bg-black/80" onClick={() => setConfirmRemove(false)} />
          <div className="relative z-50 w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Remove Device</h3>
            <p className="mt-2 text-sm text-text-secondary">
              This will permanently remove <strong>{device.hostname}</strong> and disconnect its agent. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmRemove(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  removeDevice.mutate(device.id);
                  setConfirmRemove(false);
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const offlineClass = 'text-text-secondary opacity-50';

function buildColumns(onViewDetails: (id: string) => void): Column<Device>[] {
  return [
  {
    key: 'hostname',
    header: 'Hostname',
    sortable: true,
    render: (row) => (
      <button
        onClick={() => onViewDetails(row.id)}
        className={`text-left underline decoration-transparent hover:decoration-current transition-colors ${row.status === 'Offline' ? offlineClass : 'text-text-primary'}`}
      >
        {row.hostname}
      </button>
    ),
  },
  {
    key: 'platform',
    header: 'Platform',
    sortable: true,
    render: (row) => (
      <span className={row.status === 'Offline' ? offlineClass : ''}>{row.platform}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (row) => {
      const label =
        row.status === 'PendingApproval' ? 'Awaiting user sign-in' : row.status;
      const variant =
        row.status === 'Online'
          ? 'success'
          : row.status === 'PendingApproval'
            ? 'warning'
            : 'secondary';
      return (
        <Badge variant={variant}>
          {row.status === 'Online' ? (
            <CheckCircle2 className="mr-1 h-3 w-3" />
          ) : row.status === 'PendingApproval' ? (
            <Clock className="mr-1 h-3 w-3" />
          ) : (
            <MinusCircle className="mr-1 h-3 w-3" />
          )}
          {label}
        </Badge>
      );
    },
  },
  {
    key: 'agentVersion',
    header: 'Agent Version',
    render: (row) => (
      <span className={row.status === 'Offline' ? offlineClass : ''}>{row.agentVersion ?? '-'}</span>
    ),
  },
  {
    key: 'lastHeartbeatAt',
    header: 'Last Heartbeat',
    render: (row) =>
      row.status === 'Offline'
        ? <span className={offlineClass}>-</span>
        : <AnimatedTimestamp value={row.lastHeartbeatAt} fallback="Never" />,
  },
  {
    key: 'registeredAt',
    header: 'Registered',
    render: (row) => (
      <span className={row.status === 'Offline' ? offlineClass : ''}>
        {formatDate(row.registeredAt)}
      </span>
    ),
  },
  {
    key: 'id',
    header: '',
    render: (row) => <ActionsCell device={row} onViewDetails={() => onViewDetails(row.id)} />,
  },
  ];
}

export function DevicesPage() {
  const { data: devices, isLoading } = useDevices();
  useDeviceUpdates();
  const { isPersonal, deviceLimit } = usePlan();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const columns = buildColumns((id) => setSelectedDeviceId(id));

  // Mirrors the server's count (RegisterAgentDeviceHandler): removed and revoked devices do not hold a
  // slot, so someone who reinstalls twice is not permanently stuck. Counting them here too is what keeps
  // the number on screen the same as the number that refuses the next enrolment.
  const occupied = (devices ?? []).filter(
    (d) => d.status !== 'Removed' && d.status !== 'Revoked',
  ).length;
  const atLimit = isPersonal && deviceLimit !== null && occupied >= deviceLimit;

  return (
    <div>
      <PageHeader
        title="Devices"
        description={
          isPersonal && deviceLimit !== null
            ? `${occupied} of ${deviceLimit} devices used on your free account`
            : 'Manage endpoint agents and device approvals'
        }
        actions={<AddDeviceButton />}
      />

      {/* THE PRIMARY UPSELL (story #135). A refused third device is the one moment in the free tier
          where someone has demonstrably outgrown it, so the boundary is stated on the page BEFORE they
          try - an explained limit rather than a failed enrolment. */}
      {atLimit && (
        <UpgradePrompt
          className="mb-4"
          surface="device_limit"
          title="You're using both of your free devices"
          description={
            `Your free account covers ${deviceLimit} devices. Upgrading to a team removes the limit, ` +
            'and your existing devices keep working throughout — nothing needs re-enrolling.'
          }
        />
      )}

      <PendingDevicesCard />
      <DataTable
        columns={columns}
        data={devices ?? []}
        keyExtractor={(d) => d.id}
        loading={isLoading}
        emptyMessage="No devices registered yet"
      />
      <DeviceDetailsDialog
        deviceId={selectedDeviceId}
        open={!!selectedDeviceId}
        onClose={() => setSelectedDeviceId(null)}
      />
    </div>
  );
}
